<template>
  <v-sheet v-if="canView" dark tag="section" class="gf-warnings" aria-labelledby="warnings-title">
    <header class="warnings-header">
      <div>
        <p class="eyebrow">Group Fitness</p>
        <h1 id="warnings-title">Verwarnungen</h1>
        <p class="intro">Benachrichtigungen zu nicht wahrgenommenen Kursplätzen bei bestehender Warteliste.</p>
      </div>
      <button class="refresh-button" type="button" :disabled="loading" @click="fetchWarnings">
        <v-icon dark small aria-hidden="true">mdi-refresh</v-icon>
        {{ loading ? "Wird geladen …" : "Aktualisieren" }}
      </button>
    </header>

    <v-card dark flat tag="section" class="preview-panel" aria-labelledby="preview-title" :aria-busy="actionBusy ? 'true' : 'false'">
      <div class="preview-header">
        <div>
          <h2 id="preview-title">{{ runResult ? "Ergebnis des manuellen Laufs" : "Versandvorschau" }}</h2>
          <p>Prüfe, welche Personen heute eine Nachricht erhalten würden. Die Vorschau versendet und speichert nichts.</p>
        </div>
        <div class="preview-actions">
          <button type="button" :disabled="actionBusy" @click="loadPreview">
            {{ previewLoading ? "Vorschau wird geladen …" : "Vorschau laden" }}
          </button>
          <button class="send-button" type="button" :disabled="!canExecute" @click="executeWarnings">
            {{ sending ? "Versand läuft …" : "Verwarnungen jetzt versenden" }}
          </button>
        </div>
      </div>
      <p class="preview-note">Ausführen startet den normalen Job mit den dann aktuellen Daten. Bereits verarbeitete Personen werden übersprungen.</p>
      <div v-if="actionError" class="error-message" role="alert">{{ actionError }}</div>

      <template v-if="activeRun">
        <p class="run-summary" role="status">
          {{ runResult ? "Ausgeführt" : "Stand" }}: {{ formatDate(activeRun.checkedAt) }} ·
          {{ activeRun.counts.courses }} Kurse ·
          {{ runResult ? activeRun.counts.attempted + " Personen verarbeitet" : activeRun.counts.wouldSend + " vorgesehene Empfänger" }} ·
          {{ activeRun.counts.skipped }} übersprungen
        </p>
        <p v-if="!activeRun.courses.length" class="preview-empty">Aktuell gibt es keine beendeten Kurse mit Warteliste, die für den Job infrage kommen.</p>
        <v-card v-for="course in activeRun.courses" :key="course.executionId" dark flat tag="article" class="preview-course">
          <div class="card-top"><span class="studio-label">{{ course.studioName }}</span><span>{{ course.capacity }} Plätze · {{ course.waitCount }} auf der Warteliste</span></div>
          <h3><router-link class="course-link" :to="'/kurse/' + course.executionId">{{ course.courseName }}</router-link></h3>
          <p class="course-time">{{ formatDate(course.startsAt) }} · Ende {{ formatDate(course.endsAt) }}</p>
          <p v-if="!course.recipients.length" class="preview-note">Keine nicht anwesenden Personen mit festem Kursplatz.</p>
          <ul v-else class="preview-people">
            <li v-for="person in course.recipients" :key="person.userId" class="preview-person">
              <div class="person-heading">
                <strong>{{ person.name }}</strong>
                <span class="status-badge" :class="person.status === 'would_send' ? 'status-selected' : ''">{{ recipientLabel(person) }}</span>
              </div>
              <p class="person-email">{{ person.email || "Keine E-Mail-Adresse hinterlegt" }}</p>
              <dl class="delivery-grid">
                <div v-for="channel in channels" :key="channel.key" class="delivery-cell">
                  <dt>{{ channel.label }}</dt>
                  <template v-if="person[channel.key]">
                    <dd><span class="status-badge" :class="'status-' + state(person[channel.key])">{{ statusLabel(person[channel.key], channel.key) }}</span></dd>
                    <dd v-if="statusDetail(person[channel.key])" class="status-detail">{{ statusDetail(person[channel.key]) }}</dd>
                  </template>
                  <dd v-else class="channel-preview">{{ channelPreview(person, channel.key) }}</dd>
                </div>
              </dl>
            </li>
          </ul>
          <details class="message-details">
            <summary>Vorgesehene Nachricht anzeigen</summary>
            <p class="message-title">{{ course.title }}</p>
            <p>{{ course.text }}</p>
          </details>
        </v-card>
      </template>
    </v-card>

    <h2 class="log-title">Versandprotokoll · letzte 24 Stunden</h2>
    <p class="retention-note">Einträge verschwinden nach 24 Stunden. Fehlgeschlagene oder unklare Versandversuche werden nicht wiederholt. Wenn du einen Eintrag löschst, kann die Person beim nächsten Lauf erneut benachrichtigt werden.</p>
    <div v-if="error" class="error-message" role="alert">{{ error }}</div>
    <div v-if="deleteError" class="error-message" role="alert">{{ deleteError }}</div>
    <div v-if="loading && !items.length" class="empty-state" role="status">Verwarnungen werden geladen …</div>
    <div v-else-if="!visibleItems.length && !error" class="empty-state">
      <v-icon dark size="36" color="#9cdea9" aria-hidden="true">mdi-check-circle-outline</v-icon>
      <h2>Keine Verwarnungen vorhanden</h2>
      <p>Für diese Seite liegen keine Einträge aus den letzten 24 Stunden vor.</p>
    </div>

    <div class="warnings-grid" :aria-busy="loading ? 'true' : 'false'">
      <v-card v-for="item in visibleItems" :key="item._id" dark flat tag="article" class="warning-card">
        <div class="card-top">
          <span class="studio-label">{{ item.studioName }}</span>
          <time :datetime="item.createdAt">{{ formatDate(item.createdAt) }}</time>
        </div>
        <h2>{{ item.userName || "Unbekannter Teilnehmer" }}</h2>
        <p class="email-address">{{ item.email || "Keine E-Mail-Adresse hinterlegt" }}</p>
        <router-link class="course-link" :to="'/kurse/' + item.executionId">{{ item.courseName }}</router-link>
        <p class="course-time">{{ formatDate(item.startsAt) }} · {{ item.capacity }} Plätze · {{ item.waitCount }} auf der Warteliste</p>
        <dl class="delivery-grid">
          <div v-for="channel in channels" :key="channel.key" class="delivery-cell">
            <dt>{{ channel.label }}</dt>
            <dd><span class="status-badge" :class="'status-' + state(item[channel.key])">{{ statusLabel(item[channel.key], channel.key) }}</span></dd>
            <dd v-if="statusDetail(item[channel.key])" class="status-detail">{{ statusDetail(item[channel.key]) }}</dd>
          </div>
        </dl>
        <details class="message-details">
          <summary>Nachricht anzeigen</summary>
          <p>{{ item.text }}</p>
        </details>
        <button v-if="canView" type="button" class="delete-button" :disabled="!!deletingId || loading || actionBusy"
          :aria-label="'Protokoll für ' + (item.userName || 'Teilnehmer') + ' löschen'" @click="deleteWarning(item)">
          <v-icon dark small aria-hidden="true">mdi-delete-outline</v-icon>
          {{ deletingId === item._id ? "Wird gelöscht …" : "Protokoll löschen" }}
        </button>
      </v-card>
    </div>

    <nav v-if="page > 1 || total > pageSize" class="pagination" aria-label="Verwarnungen durchblättern">
      <button type="button" :disabled="loading || page <= 1" @click="changePage(page - 1)">Zurück</button>
      <span>Seite {{ page }} von {{ pageCount }}</span>
      <button type="button" :disabled="loading || page >= pageCount" @click="changePage(page + 1)">Weiter</button>
    </nav>
  </v-sheet>
</template>

<script>
import api from "@/api/groupFitnessApi"
import userService from "@/services/userService"

const dateFormat = new Intl.DateTimeFormat("de-CH", {
  timeZone: "Europe/Zurich", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
})

export default {
  name: "GroupFitnessWarnings",
  data() {
    return { items: [], total: 0, page: 1, pageSize: 50, loading: false, error: "", clock: Date.now(), timer: null,
      preview: null, runResult: null, previewLoading: false, sending: false, actionError: "", actionId: 0, deletingId: null, deleteError: "", deleteId: 0,
      requestId: 0, channels: [{ key: "mail", label: "E-Mail" }, { key: "push", label: "Push" }] }
  },
  computed: {
    canView() { return userService.hasRight("gf_warnings") },
    visibleItems() { return this.items.filter(item => new Date(item.expiresAt).getTime() > this.clock) },
    pageCount() { return Math.max(1, Math.ceil(this.total / this.pageSize)) },
    actionBusy() { return this.previewLoading || this.sending || !!this.deletingId },
    activeRun() { return this.runResult || this.preview },
    canExecute() { return this.canView && !this.actionBusy && !this.runResult && !!this.preview && this.preview.counts.wouldSend > 0 },
  },
  created() {
    this.fetchWarnings()
    this.timer = setInterval(() => { this.clock = Date.now() }, 1000)
  },
  beforeDestroy() {
    clearInterval(this.timer)
    this.requestId++
    this.actionId++
    this.deleteId++
  },
  methods: {
    async deleteWarning(item) {
      if (!this.canView || this.deletingId || this.loading || this.actionBusy) return
      const deleteId = ++this.deleteId
      this.deletingId = item._id
      this.deleteError = ""
      try {
        await api.deleteGFWarning(item._id)
      } catch (error) {
        if (deleteId !== this.deleteId) return
        const status = error.response && error.response.status
        if (status !== 404) {
          this.deleteError = [401, 403].includes(status)
            ? "Dir fehlt die Berechtigung zum Löschen."
            : "Der Protokolleintrag konnte nicht gelöscht werden. Bitte lade die Liste erneut."
          return
        }
      } finally {
        if (deleteId === this.deleteId) this.deletingId = null
      }
      if (deleteId !== this.deleteId) return
      // The reservation is gone: discard stale preview/results before another run.
      this.actionId++
      this.preview = null
      this.runResult = null
      await this.fetchWarnings()
    },
    async fetchWarnings() {
      if (!this.canView) { this.$emit("finishedloading"); return }
      const requestId = ++this.requestId
      this.loading = true
      this.error = ""
      try {
        const result = await api.fetchGFWarnings(this.page)
        if (requestId !== this.requestId) return
        this.items = result.items
        this.total = result.total
        this.pageSize = result.pageSize
        this.clock = Date.now()
        if (this.page > this.pageCount) {
          this.page = this.pageCount
          return this.fetchWarnings()
        }
      } catch (error) {
        if (requestId !== this.requestId) return
        this.items = []
        this.error = error.response && [401, 403].includes(error.response.status)
          ? "Dir fehlt die Berechtigung für diese Übersicht."
          : "Die Verwarnungen konnten nicht geladen werden. Bitte versuche es erneut."
      } finally {
        if (requestId === this.requestId) {
          this.loading = false
          this.$emit("finishedloading")
        }
      }
    },
    async loadPreview() {
      if (!this.canView || this.actionBusy) return
      const actionId = ++this.actionId
      this.previewLoading = true
      this.actionError = ""
      this.preview = null
      this.runResult = null
      try {
        const result = await api.previewGFWarnings()
        if (actionId !== this.actionId) return
        this.preview = result
      } catch (error) {
        if (actionId !== this.actionId) return
        this.actionError = error.response && [401, 403].includes(error.response.status)
          ? "Dir fehlt die Berechtigung für diese Vorschau."
          : "Die Vorschau konnte nicht geladen werden. Es wurde nichts versendet."
      } finally {
        if (actionId === this.actionId) this.previewLoading = false
      }
    },
    async executeWarnings() {
      if (!this.canExecute) return
      const actionId = ++this.actionId
      this.sending = true
      this.actionError = ""
      try {
        const result = await api.executeGFWarnings()
        if (actionId !== this.actionId) return
        this.runResult = result
      } catch (error) {
        if (actionId !== this.actionId) return
        const status = error.response && error.response.status
        if ([401, 403].includes(status)) {
          this.actionError = "Dir fehlt die Berechtigung für den Versand."
        } else {
          this.actionError = "Der Lauf konnte nicht vollständig bestätigt werden. Prüfe das Versandprotokoll oder lade die Vorschau erneut. Bereits reservierte Versuche werden nicht wiederholt."
        }
      } finally {
        if (actionId === this.actionId) {
          this.preview = null
          this.sending = false
          this.page = 1
          await this.fetchWarnings()
        }
      }
    },
    recipientLabel(person) {
      return { would_send: "Vorgesehen", attempted: "Verarbeitet", already_attempted: "Bereits verarbeitet",
        user_unavailable: "Benutzer nicht verfügbar", no_longer_eligible: "Nicht mehr zutreffend", outside_window: "Ausserhalb des Zeitfensters" }[person.status] || "Übersprungen"
    },
    channelPreview(person, channel) {
      if (person.status !== "would_send") return "Kein neuer Versand"
      return channel === "mail" ? (person.email ? "E-Mail vorgesehen" : "Keine E-Mail-Adresse")
        : (person.deviceCount ? `Push an ${person.deviceCount} Gerät${person.deviceCount === 1 ? "" : "e"} vorgesehen` : "Kein Push-Gerät hinterlegt")
    },
    changePage(page) { this.page = page; this.fetchWarnings() },
    formatDate(value) { return value ? dateFormat.format(new Date(value)) : "–" },
    state(channel) {
      if (!channel) return "unknown"
      if (channel.status === "attempting" && this.clock - new Date(channel.attemptedAt).getTime() > 120000) return "unknown"
      return channel.status
    },
    statusLabel(channel, key) {
      const status = this.state(channel)
      if (status === "sent") return key === "push" ? "An Pushdienst übergeben" : "Versendet"
      return { pending: "Nicht begonnen", attempting: "In Verarbeitung", partial: "Teilweise übergeben",
        failed: "Fehlgeschlagen", skipped: "Nicht möglich", unknown: "Versandstatus unklar" }[status] || "Versandstatus unklar"
    },
    statusDetail(channel) {
      if (!channel) return ""
      if (channel.code === "no_email") return "Keine E-Mail-Adresse hinterlegt."
      if (channel.code === "no_devices") return "Kein Push-Gerät hinterlegt."
      if (channel.successCount !== undefined) return `${channel.successCount} erfolgreich · ${channel.failureCount || 0} nicht bestätigt`
      return ""
    },
  },
}
</script>

<style scoped>
.gf-warnings {
  --warnings-bg: #1e1e1e;
  --warnings-surface: #272727;
  --warnings-raised: #303530;
  --warnings-border: #465047;
  --warnings-text: #f3f5f3;
  --warnings-muted: #b6c2ba;
  --warnings-accent: #9cdea9;
  padding: 1.5rem; color: var(--warnings-text); background: var(--warnings-bg);
  border-radius: 1rem; font-size: 1rem; line-height: 1.5;
}
.warnings-header { display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: space-between; gap: 1.25rem; }
.warnings-header > div { flex: 1 1 22rem; min-width: 0; }
.eyebrow { color: var(--warnings-muted); font-size: .875rem; margin: 0 0 .5rem; }
.gf-warnings h1 { font-size: 1.75rem; line-height: 1.25; margin: 0 0 .75rem; font-weight: 700; }
.intro { margin: 0; max-width: 44rem; }
.gf-warnings button { display: inline-flex; align-items: center; justify-content: center; gap: .5rem; padding: .625rem 1rem; min-height: 2.75rem; border: 1px solid var(--warnings-border); border-radius: .625rem; color: var(--warnings-text); background: var(--warnings-surface); font: inherit; cursor: pointer; }
.gf-warnings button:not(:disabled):hover { background: var(--warnings-raised); border-color: var(--warnings-accent); }
.gf-warnings button:disabled { opacity: .55; cursor: default; }
.gf-warnings button:focus-visible, .gf-warnings summary:focus-visible, .course-link:focus-visible { outline: 3px solid var(--warnings-accent); outline-offset: 3px; }
.gf-warnings .delete-button { margin-top: 1rem; color: #ffc1b8; }
.retention-note { margin: 1.25rem 0; color: var(--warnings-muted); font-size: .875rem; }
.error-message { padding: 1rem; margin-bottom: 1rem; background: #3f2424; border: 1px solid #8a4d46; border-radius: .625rem; color: #ffc1b8; }
.warnings-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 26rem), 1fr)); gap: 1rem; }
.gf-warnings .warning-card { min-width: 0; padding: 1.25rem; background: var(--warnings-surface); border: 1px solid var(--warnings-border); border-radius: .875rem; overflow-wrap: anywhere; }
.card-top { display: flex; flex-wrap: wrap; justify-content: space-between; gap: .5rem; margin-bottom: 1rem; color: var(--warnings-muted); font-size: .75rem; }
.studio-label { font-weight: 700; }
.warning-card h2 { font-size: 1.125rem; line-height: 1.4; font-weight: 700; margin: 0; }
.email-address { margin: .25rem 0 1rem; color: var(--warnings-muted); font-size: .875rem; }
.gf-warnings .course-link { color: var(--warnings-accent); font-size: 1rem; font-weight: 600; text-decoration: underline; text-underline-offset: .2em; }
.course-time { font-size: .875rem; color: var(--warnings-muted); margin: .5rem 0 1rem; }
.delivery-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 11rem), 1fr)); gap: .75rem; margin: 0; }
.delivery-cell { padding: .75rem; background: var(--warnings-raised); border-radius: .5rem; min-width: 0; }
.delivery-cell dt { font-size: .875rem; font-weight: 600; margin-bottom: .5rem; }
.delivery-cell dd { margin: 0; }
.status-badge { display: inline-block; max-width: 100%; border-radius: .375rem; padding: .25rem .5rem; font-size: .75rem; font-weight: 700; color: #d6dfd8; background: #3a413c; }
.status-sent { color: #a6e8b7; background: #193c28; }
.status-failed { color: #ffc1b8; background: #4a2525; }
.status-partial, .status-unknown { color: #ffe0a0; background: #4b3d21; }
.delivery-cell .status-detail { font-size: .875rem; color: var(--warnings-muted); margin-top: .5rem; }
.message-details { margin-top: 1rem; font-size: .875rem; }
.message-details summary { cursor: pointer; color: var(--warnings-accent); padding: .25rem 0; }
.message-details p { white-space: pre-line; font-size: 1rem; margin: .75rem 0 0; }
.empty-state { text-align: center; border: 1px dashed var(--warnings-border); border-radius: .875rem; padding: 2.5rem 1rem; background: var(--warnings-surface); }
.empty-state h2 { font-size: 1.25rem; margin: .75rem 0 .5rem; }
.empty-state p { margin: 0; color: var(--warnings-muted); }
.pagination { display: flex; flex-wrap: wrap; justify-content: center; align-items: center; gap: 1rem; margin-top: 1.5rem; }
.pagination span { font-size: .875rem; }
.gf-warnings .preview-panel { margin-top: 1.5rem; padding: 1.25rem; background: var(--warnings-surface); border: 1px solid var(--warnings-border); border-radius: .875rem; }
.preview-header { display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: space-between; gap: 1rem; }
.preview-header > div:first-child { flex: 1 1 22rem; min-width: 0; }
.preview-header h2, .log-title { font-size: 1.25rem; line-height: 1.4; margin: 0 0 .5rem; }
.preview-header p { margin: 0; }
.preview-actions { display: flex; flex-wrap: wrap; gap: .75rem; }
.gf-warnings .send-button { color: #fff; background: #28673d; border-color: var(--warnings-accent); }
.gf-warnings .send-button:not(:disabled):hover { background: #327b49; border-color: var(--warnings-accent); }
.preview-note, .run-summary { font-size: .875rem; color: var(--warnings-muted); margin: .875rem 0; }
.run-summary { font-weight: 600; }
.preview-empty { margin: 1rem 0 0; }
.gf-warnings .preview-course { margin-top: 1rem; padding: 1rem; border: 1px solid var(--warnings-border); border-radius: .75rem; min-width: 0; overflow-wrap: anywhere; }
.preview-course h3 { margin: 0; font-size: 1.125rem; }
.preview-course .course-link { font-size: 1.125rem; }
.preview-people { list-style: none; margin: 0; padding: 0; display: grid; gap: 1rem; }
.preview-person { padding-top: 1rem; border-top: 1px solid var(--warnings-border); }
.person-heading { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: .5rem; }
.person-email { font-size: .875rem; color: var(--warnings-muted); margin: .25rem 0 .75rem; }
.status-selected { color: #a6e8b7; background: #193c28; }
.channel-preview { font-size: .875rem; color: var(--warnings-muted); }
.message-title { font-weight: 600; }
.log-title { margin-top: 2rem; }
@media (max-width: 600px) { .gf-warnings { padding: 1rem; } .gf-warnings .warning-card { padding: 1rem; } }
</style>
